import { ChevronDown, Plus } from 'lucide-react'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptySearchResult } from '@/components/EmptySearchResult'
import InputSearch from '@/components/InputSearch'
import { Table } from '@/components/Table'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  DEFAULT_SORT_COLUMN,
  DEFAULT_SORT_DIRECTION,
  getUserManagementColumns,
  MIN_ADD_BUTTON_WIDTH,
  MIN_ROLE_DROPDOWN_WIDTH,
  MIN_STATUS_DROPDOWN_WIDTH,
  useGetRoles,
  UserManagementAction,
  UserManagementUser,
  useUserManagementList,
  useUserManagementStore,
} from '@/features/user-management'
import {
  DeleteUserDialog,
  EditUserDialog,
  EmptyState,
  InviteUsersDialog,
  UserDetailDrawer,
} from '@/features/user-management/components'
import { useDebounce } from '@/hooks/useDebounce'
import { SortOrder } from '@/types/common'
import {
  DEFAULT_PAGE,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_DELAY_MS,
} from '@/utils/constants'

import { UserStatus } from '../../schema'

const UserManagementPage: React.FC = () => {
  const { t } = useTranslation()

  const {
    searchText,
    filters,
    page,
    sort,
    setSearchText,
    setFilter,
    setPage,
    setSort,
    setEditingUser,
    setDeletingUser,
    setSelectedUser,
    openInviteModal,
    currentUserRole,
    reset,
  } = useUserManagementStore()

  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)

  const searchQuery = useMemo(() => {
    const trimmed = debouncedSearchText?.trim() || ''
    return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ''
  }, [debouncedSearchText])

  const {
    data: {
      data = [],
      pagination = {
        page: 0,
        total_pages: 0,
        total_items: 0,
        page_size: 0,
        has_next: false,
        has_previous: false,
      },
    } = {},
    isLoading,
    refetch,
  } = useUserManagementList({
    search: searchQuery,
    filters,
    page,
    sort,
  })

  useEffect(() => {
    return () => {
      reset()
    }
  }, [reset])

  useEffect(() => {
    if (!Number.isNaN(page)) {
      refetch()
    }
  }, [page, refetch])

  const handleActionClick = useCallback(
    (action: UserManagementAction, item: UserManagementUser) => {
      switch (action) {
        case 'view-detail':
          setSelectedUser(item)
          break
        case 'edit':
          setEditingUser(item)
          break
        case 'delete':
          setDeletingUser(item)
          break
        default:
          break
      }
    },
    [setSelectedUser, setEditingUser, setDeletingUser]
  )

  const columns = useMemo(
    () => getUserManagementColumns(handleActionClick, t, currentUserRole),
    [currentUserRole, handleActionClick, t]
  )

  const handleSortChange = useCallback(
    (colId: keyof UserManagementUser | null, dir: SortOrder) => {
      // If trying to reset (dir is null means reset)
      if (dir === null) {
        // Check if the CURRENT sort is on the default column at default direction
        const isCurrentlyAtDefault =
          sort.column === DEFAULT_SORT_COLUMN &&
          sort.direction === DEFAULT_SORT_DIRECTION

        // If we're already at default and trying to reset, switch to DESC instead
        if (isCurrentlyAtDefault) {
          setSort({
            column: DEFAULT_SORT_COLUMN,
            direction: 'desc' as SortOrder,
          })
        } else {
          // Otherwise reset to default
          setSort({
            column: DEFAULT_SORT_COLUMN as keyof UserManagementUser,
            direction: DEFAULT_SORT_DIRECTION as SortOrder,
          })
        }
      } else {
        setSort({
          column: colId ? String(colId) : DEFAULT_SORT_COLUMN,
          direction: dir,
        })
      }
    },
    [setSort, sort.column, sort.direction]
  )

  const handleInviteUser = () => {
    openInviteModal()
  }

  const hasFilters =
    searchQuery ||
    (filters.role_id && filters.role_id.length > 0) ||
    (filters.status && filters.status.length > 0)
  const isEmpty = data.length === 0
  const isTrulyEmpty = isEmpty && !hasFilters && !isLoading

  const { availableRoles } = useGetRoles({
    scopeLevel: null,
    enabled: true,
  })

  const noDataMessage = useMemo(() => {
    if (hasFilters && isEmpty) {
      return <EmptySearchResult translationKey="userManagement" />
    }
    return null
  }, [hasFilters, isEmpty])

  return (
    <div className="flex flex-col space-y-6">
      <div className="flex justify-between flex-shrink-0">
        <div>
          <h1 className="m-0 mb-1 font-semibold text-display-xs">
            {t('userManagement.page.title')}
          </h1>
          <p className="m-0 text-md text-muted-foreground">
            {t('userManagement.page.description')}
          </p>
        </div>
        {isTrulyEmpty ? null : (
          <div>
            <Button
              onClick={handleInviteUser}
              className={`text-sm border border-solid border-transparent min-w-[${MIN_ADD_BUTTON_WIDTH}px]`}
              leadingIcon={<Plus size={20} />}
            >
              {t('userManagement.actions.inviteUser')}
            </Button>
          </div>
        )}
      </div>

      {isTrulyEmpty ? (
        <EmptyState onInviteClick={handleInviteUser} />
      ) : (
        <>
          {/* MARK: Search */}
          <div className="flex flex-wrap justify-between flex-shrink-0 gap-3">
            <InputSearch
              searchText={searchText}
              setSearchText={setSearchText}
              placeholder={t('userManagement.filters.search')}
            />

            {/* MARK: Filters */}
            <div className="flex flex-wrap gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outlined"
                    className={`min-w-[${MIN_ROLE_DROPDOWN_WIDTH}px] justify-between text-primary-900`}
                    trailingIcon={
                      <ChevronDown
                        size={16}
                        className="text-foreground-fg-quaternary-400"
                      />
                    }
                  >
                    {filters.role_id && filters.role_id.length > 0
                      ? availableRoles.find(
                          (role) => role.value === filters.role_id![0]
                        )?.label
                      : t('userManagement.filters.role')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className={`min-w-[${MIN_ROLE_DROPDOWN_WIDTH}px]`}
                >
                  <DropdownMenuItem
                    onClick={() => setFilter('role_id', undefined)}
                  >
                    {t('userManagement.filters.allFilter')}
                  </DropdownMenuItem>
                  {availableRoles.map((role) => (
                    <DropdownMenuItem
                      key={role.value}
                      onClick={() => setFilter('role_id', [role.value])}
                    >
                      {role.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outlined"
                    className={`min-w-[${MIN_STATUS_DROPDOWN_WIDTH}px] justify-between text-primary-900`}
                    trailingIcon={
                      <ChevronDown
                        size={16}
                        className="text-foreground-fg-quaternary-400"
                      />
                    }
                  >
                    {filters.status && filters.status.length > 0
                      ? filters.status
                          .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
                          .join(', ')
                      : t('userManagement.filters.status')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className={`min-w-[${MIN_STATUS_DROPDOWN_WIDTH}px]`}
                >
                  <DropdownMenuItem
                    onClick={() => setFilter('status', undefined)}
                  >
                    {t('userManagement.filters.allFilter')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setFilter('status', [UserStatus.ACTIVE])}
                  >
                    Active
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setFilter('status', [UserStatus.PENDING])}
                  >
                    Pending
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            <Table
              columns={columns}
              data={data ?? []}
              page={(pagination?.page ?? DEFAULT_PAGE) - 1}
              totalPages={pagination?.total_pages ?? 0}
              onPageChange={setPage}
              isLoading={isLoading}
              noDataMessage={noDataMessage}
              orderBy={sort.column as keyof UserManagementUser | null}
              order={sort.direction}
              onSortChange={handleSortChange}
              onRowClick={(row) => handleActionClick('view-detail', row)}
              roundedTop
            />
          </div>
        </>
      )}

      <UserDetailDrawer />
      <EditUserDialog />
      <DeleteUserDialog />
      <InviteUsersDialog />
    </div>
  )
}

export default UserManagementPage
